import { Modal, useModals } from 'almanac-ui/Modal'
import { useCallback } from 'react'
import { useId } from '@react-aria/utils'
import { useUpdateBrandPlanSchedule } from 'hooks/queries/schedules/useUpdateBrandPlanSchedule'
import {
  EditCampaignScheduleModalProps,
  EditScheduleFormValues,
  EditScheduleModalProps
} from 'screens/Campaign/Overview/Types'
import { Form, Formik } from 'formik'
import dayjs from 'dayjs'
import * as yup from 'yup'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { ReviewTime } from 'screens/Campaign/Overview/EditScheduleModal/ReviewTime'
import Styles from './EditScheduleModal.module.scss'
import { Schedule } from 'screens/Campaigns/components/AddCampaignModal/CreateCampaign/Schedule'
import { WrapperCard } from 'screens/Campaigns/components/AddCampaignModal/WrapperCard'
import { DaysToPost } from 'screens/Campaign/Overview/EditScheduleModal/DaysToPost'

const validationSchema = yup.object().shape({
  week_days: yup.array().of(yup.string()).min(1, 'Please select at least one day').required(),
  days_before_review: yup
    .number()
    .required('Required')
    .min(0, 'Must be positive')
    .when('start_date', ([value]) => {
      const startDate = dayjs(value)
      const maxDays = dayjs(startDate).diff(dayjs(), 'day')
      return yup.number().max(maxDays, 'Review date must be in the future')
    }),
})

export const EditScheduleModal = ({ schedule, onSubmit }: EditScheduleModalProps) => {
  const { closeModal } = useModals()
  const id = useId()

  const approvalDate = schedule.approval_date || dayjs(schedule.start_date).subtract(2, 'day').format('YYYY-MM-DD')

  return (
    <Formik
      initialValues={{
        days_before_review: Math.min(dayjs(schedule.start_date).diff(dayjs(approvalDate), 'day'), 4),
        week_days: schedule.week_days,
        length: schedule.length || 1,
        start_date: schedule.start_date,
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {({ submitForm }) => (
        <Modal.Root size="sm" blaze aria-labelledby={id}>
          <Modal.Header title="Set campaign schedule" />
          <Modal.Content>
            <Form className={Styles.container}>
              <WrapperCard>
                <Schedule startDateField="start_date" />
              </WrapperCard>
              <DaysToPost />
              <ReviewTime />
            </Form>
          </Modal.Content>
          <Modal.Footer>
            <Modal.FooterContent slot="left">
              <Modal.FooterButton onPress={() => closeModal()} size="lg" variant="tertiary">
                Cancel
              </Modal.FooterButton>
            </Modal.FooterContent>
            <Modal.FooterContent slot="right">
              <Modal.FooterButton onPress={() => submitForm()} size="lg" variant="primary">
                Save
              </Modal.FooterButton>
            </Modal.FooterContent>
          </Modal.Footer>
        </Modal.Root>
      )}
    </Formik>
  )
}

export const EditCampaignScheduleModal = ({ campaign }: EditCampaignScheduleModalProps) => {
  const { closeModal } = useModals()

  const { mutate } = useUpdateBrandPlanSchedule(
    { id: campaign.id },
    {
      onSuccess: () => closeModal(),
      onError: () => {
        showErrorMessageToast('There was an error updating the campaign. Please try again.')
      },
    },
  )

  const handleSubmit = useCallback(
    (values: EditScheduleFormValues) => {
      const { days_before_review, ...rest } = values
      mutate({
        approval_date: dayjs(values.start_date).subtract(days_before_review, 'day').format('YYYY-MM-DD'),
        ...rest,
      })
    },
    [mutate],
  )

  return (
    <EditScheduleModal
      schedule={{ ...campaign, approval_date: campaign.approval_date || dayjs(campaign.start_date).subtract(2, 'day').format('YYYY-MM-DD') }}
      onSubmit={handleSubmit}
    />
  )
}
